import type { SearchSpecies, Species } from '../types'
import type { FC } from 'react'
import { Link } from '@tanstack/react-router'
export {
  SpeciesDataCard,
  AverageContentDataCard,
  makeTitleCase,
} from './DataCard'
export { Badge } from './Badge'

const speciesTypeGuard = (
  species: Species | SearchSpecies,
): species is Species =>
  'authority' in species || 'infraspecies_authority' in species

export const SpeciesName: FC<{
  species: Species | SearchSpecies
  className?: string
  withAuthor?: boolean
}> = ({ species, className = '', withAuthor }) => (
  <div className={`italic ${className}`}>
    {species.genus} {species.epithet}{' '}
    <span className="not-italic">
      {withAuthor && speciesTypeGuard(species) && (
        <span>{species.authority} </span>
      )}
      {species.infraspecies_rank && species.infraspecies_rank}
    </span>{' '}
    {species.infraspecies_epithet && species.infraspecies_epithet}
    {withAuthor && speciesTypeGuard(species) && (
      <span className="not-italic"> {species.infraspecies_authority}</span>
    )}
  </div>
)

const SpeciesListItem: FC<{ species: SearchSpecies; className?: string }> = ({
  species,
  className = '',
}) => {
  return (
    <Link to={`/species/${species.id}`} key={species.id}>
      <li
        className={`flex w-full cursor-pointer justify-between px-4 py-2 ${className} `}
      >
        <SpeciesName species={species} />
        <span>
          {species.has_morphology && (
            <span className="mr-2" title="Morphology info">
              📏
            </span>
          )}
          {species.has_storage_behaviour && (
            <span className="mr-2" title="Storage info">
              📦
            </span>
          )}
          {species.has_germination && (
            <span className="mr-2" title="Germination info">
              🌱
            </span>
          )}
          {species.has_dispersal && (
            <span className="mr-2" title={'Dispersal info'}>
              🐿
            </span>
          )}
          {species.has_oil && (
            <span className="mr-2" title="Oil content info">
              🟡
            </span>
          )}
          {species.has_protein && (
            <span className="mr-2" title="Protein content info">
              🥩
            </span>
          )}
          {species.has_seed_weights && (
            <span className="mr-2" title="Seed weight info">
              ⚖️
            </span>
          )}
        </span>
      </li>
    </Link>
  )
}

export const SpeciesList: FC<{ speciesList: SearchSpecies[] }> = ({
  speciesList,
}) => (
  <div className="mt-6 flex-col sm:w-full">
    <h4 className="text-1xl font-bold text-gray-800">
      Results ({speciesList.length})
    </h4>
    <ul className="mt-2 rounded-lg border border-teal-200 bg-white text-sm font-medium text-gray-900">
      {speciesList.map((species, index) => (
        <SpeciesListItem
          key={species.id}
          species={species}
          className={
            index === speciesList.length - 1
              ? 'rounded-b-lg '
              : 'rounded-t-lg border-b border-teal-200'
          }
        />
      ))}
    </ul>
  </div>
)

export const Spinner: FC<{ size?: number }> = ({ size = 5 }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    className={`h-${size} w-${size} animate-spin fill-serblue-light text-gray-200`}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
      opacity=".25"
    />
    <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z">
      <animateTransform
        attributeName="transform"
        type="rotate"
        dur="0.75s"
        values="0 12 12;360 12 12"
        repeatCount="indefinite"
      />
    </path>
  </svg>
)
